<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import {
  UiText,
  UiButton,
  UiTabs,
  UiTabsItem,
  UiDialog,
  UiInputField,
  UiSelect,
  useToaster,
  UiIcon,
} from "@shopexperts/shared";
import type {
  ChatMessageAttachment,
  NoteKind,
  ProjectMediaResource,
} from "@/v2Projects/types";
import { getConvexAuthInfo, projectFns, runProjectsConvexMutation } from "@/v2Projects/convexClient";
import { addConvexBreadcrumb, captureConvexError } from "@/v2Projects/telemetry";
import { reportProjectUserError, type UserErrorMessage } from "@/v2Projects/userErrors";
import { useProjectMessages } from "@/v2Projects/composables/useProjectMessages";
import ChatFeed from "@/v2Projects/components/ChatFeed.vue";
import ChatComposer from "@/v2Projects/components/ChatComposer.vue";
import AdminChatSidebar from "@/app/components/admin-projects/AdminChatSidebar.vue";
import AdminProjectInfoTab from "@/app/components/admin-projects/AdminProjectInfoTab.vue";
import { useAdminProjectDetail } from "@/app/composables/admin-projects/useAdminProjectDetail";
import QuoteDetailStrip from "@/v2Projects/components/QuoteDetailStrip.vue";
import { formatQuoteTotal } from "@/v2Projects/utils/quotePresentation";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";
import type {
  ProjectParticipantGroup,
  ProjectParticipantRole,
  ProjectStatus,
  QuotePaymentStatus,
  QuoteStatus,
} from "@shopexperts/legacy-api";

const route = useRoute();
const router = useRouter();
const toaster = useToaster();

const projectId = computed(() => Number(route.params.id));
const activeTab = ref("chatroom");
const isDescriptionExpanded = ref(false);

const {
  project,
  isLoading,
  error,
  refetch,
  updateProjectStatus,
  updateQuoteStatus,
  updateQuotePaymentStatus,
  addParticipant,
  removeParticipant,
  unmatchProject,
} = useAdminProjectDetail(projectId);

const {
  messages,
  threadId,
  isReady: areMessagesReady,
  isLoadingMore,
  loadError: messagesLoadError,
  hasMore,
  loadMore,
  refresh: refreshMessages,
} = useProjectMessages(projectId);
const projectLoadError = ref<UserErrorMessage | null>(null);

watch(error, (nextError) => {
  projectLoadError.value = nextError
    ? reportProjectUserError("loadProject", nextError, {
        projectId: projectId.value,
        side: "admin",
      })
    : null;
}, { immediate: true });

const authInfo = computed(() => getConvexAuthInfo());
const currentUserId = computed(() => authInfo.value?.userId ?? 0);
const currentOrgId = computed(() => authInfo.value?.orgId ?? 0);

const projectStatusDraft = ref<ProjectStatus>("pending");
const quoteStatusDrafts = ref<Record<number, QuoteStatus>>({});
const quotePaymentDrafts = ref<Record<number, QuotePaymentStatus | "">>({});

const isAddParticipantDialogOpen = ref(false);
const newParticipantOrgId = ref("");
const newParticipantGroup = ref<ProjectParticipantGroup>("client");
const newParticipantRole = ref<ProjectParticipantRole>("viewer");
const newParticipantExpiry = ref("");

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
onClickOutside(menuRef, () => { menuOpen.value = false; });

const menuItems = computed(() => [
  { key: "pending", label: "Set Pending", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "quoted", label: "Set Quoted", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "in_progress", label: "Set In Progress", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "submitted", label: "Set Submitted", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "completed", label: "Set Completed", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "cancelled", label: "Set Cancelled", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "rejected", label: "Set Rejected", hidden: !project.value?.admin_actions.can_override_project_status },
  { key: "unmatch", label: "Unmatch Project", variant: "danger" as const, hidden: !project.value?.admin_actions.can_unmatch },
].filter((item) => !item.hidden));

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

async function handleMenuAction(action: string) {
  menuOpen.value = false;

  if (action === "unmatch") {
    await handleUnmatch();
    return;
  }

  await updateProjectStatus.mutateAsync(action as ProjectStatus);
  toaster.create({ title: "Project status updated", type: "success" });
}

if (project.value) {
  projectStatusDraft.value = project.value.project_status;
}

const statusPillClass = computed(() => "bg-blue-50 text-blue-700 border-blue-200");

async function applyQuoteStatus(quoteId: number) {
  await updateQuoteStatus.mutateAsync({
    quoteId,
    status: quoteStatusDrafts.value[quoteId]!,
  });
  toaster.create({ title: "Quote status updated", type: "success" });
}

async function applyQuotePaymentStatus(quoteId: number) {
  await updateQuotePaymentStatus.mutateAsync({
    quoteId,
    paymentStatus: (quotePaymentDrafts.value[quoteId] || null) as QuotePaymentStatus | null,
  });
  toaster.create({ title: "Quote payment status updated", type: "success" });
}

async function handleRemoveParticipant(participantId: number) {
  await removeParticipant.mutateAsync(participantId);
  toaster.create({ title: "Participant removed", type: "success" });
}

async function handleUnmatch() {
  await unmatchProject.mutateAsync();
  toaster.create({ title: "Project unmatched", type: "success" });
}

async function handleSendMessage(input: {
  threadId: string;
  content: string;
  attachments: ChatMessageAttachment[];
}) {
  addConvexBreadcrumb({
    operation: "admin.chat.send_message_started",
    projectId: projectId.value,
    threadId: input.threadId,
    side: "admin",
    functionName: "chat.mutations.sendMessage",
    counts: { attachments: input.attachments.length },
  });
  return await runProjectsConvexMutation(
    projectFns.sendProjectMessage,
    {
      thread_id: input.threadId as never,
      content: input.content,
      attachments: input.attachments,
    },
    {
      operation: "admin.chat.send_message",
      functionName: "chat.mutations.sendMessage",
      onError: (error, context) => {
        captureConvexError(error, {
          operation: "admin.chat.send_message_failed",
          projectId: projectId.value,
          threadId: input.threadId,
          side: "admin",
          level: context.isRecoverable ? "warning" : "error",
          functionName: "chat.mutations.sendMessage",
          auth: {
            isRecoverable: context.isRecoverable,
            recoveryAttempt: context.recoveryAttempt,
          },
          counts: { attachments: input.attachments.length },
        });
      },
    },
  );
}

async function handleSendNote(input: {
  threadId: string;
  noteKind: NoteKind;
  content: string;
  attachments: ChatMessageAttachment[];
}) {
  addConvexBreadcrumb({
    operation: "admin.chat.send_note_started",
    projectId: projectId.value,
    threadId: input.threadId,
    side: "admin",
    functionName: "chat.mutations.sendNote",
    counts: { attachments: input.attachments.length },
    extra: { noteKind: input.noteKind },
  });
  return await runProjectsConvexMutation(
    projectFns.sendProjectNote,
    {
      thread_id: input.threadId as never,
      note_kind: input.noteKind,
      content: input.content,
      attachments: input.attachments,
    },
    {
      operation: "admin.chat.send_note",
      functionName: "chat.mutations.sendNote",
      onError: (error, context) => {
        captureConvexError(error, {
          operation: "admin.chat.send_note_failed",
          projectId: projectId.value,
          threadId: input.threadId,
          side: "admin",
          level: context.isRecoverable ? "warning" : "error",
          functionName: "chat.mutations.sendNote",
          auth: {
            isRecoverable: context.isRecoverable,
            recoveryAttempt: context.recoveryAttempt,
          },
          counts: { attachments: input.attachments.length },
          extra: { noteKind: input.noteKind },
        });
      },
    },
  );
}

async function handleUpload(_input: { files: File[]; actingOrgId: number }): Promise<ProjectMediaResource[]> {
  toaster.create({
    title: "Admin uploads are not wired yet",
    description: "Message and admin note sending work now, but attachment uploads still need an admin endpoint.",
    type: "warning",
  });
  throw new Error("Admin uploads are not yet supported");
}

async function submitParticipantDialog() {
  const orgId = Number(newParticipantOrgId.value);
  if (!Number.isFinite(orgId) || orgId <= 0) {
    toaster.create({ title: "Enter a valid organization id", type: "error" });
    return;
  }

  await addParticipant.mutateAsync({
    org_id: orgId,
    participant_group: newParticipantGroup.value,
    participant_role: newParticipantRole.value,
    expires_at: newParticipantExpiry.value || null,
  });

  toaster.create({ title: "Participant added", type: "success" });
  newParticipantOrgId.value = "";
  newParticipantGroup.value = "client";
  newParticipantRole.value = "viewer";
  newParticipantExpiry.value = "";
  isAddParticipantDialogOpen.value = false;
}

// Use real history navigation so the previous Leads & Projects URL (with
// active tab + scroll position) is restored. Fall back to pushing the
// manager route if the user landed here directly (no history).
function goBack() {
  if (window.history.state?.back) {
    router.back();
  } else {
    router.push({ name: "admin-leads-projects" });
  }
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
</script>

<template>
  <main class="flex-1 min-h-0 h-full w-full overflow-hidden flex flex-col bg-secondary">
    <div v-if="isLoading" class="flex-1 flex flex-col items-center justify-center gap-3">
      <div class="w-7 h-7 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
      <UiText as="p" variant="p-small" class="text-gray-500">
        Loading project...
      </UiText>
    </div>

    <div v-else-if="error" class="flex-1 flex flex-col items-center justify-center gap-3">
      <div class="bg-white border border-gray-200 rounded-xl p-6 max-w-md text-center space-y-3">
        <UiText as="h3" variant="p" class="font-semibold text-gray-900">
          The project workspace could not be opened
        </UiText>
        <UiText as="p" variant="p-small" class="text-gray-500">
          {{ projectLoadError?.description }}
        </UiText>
        <UiButton variant="outline" label="Try Again" @click="refetch" />
      </div>
    </div>

    <template v-else-if="project">
      <div class="px-5 pt-4 pb-0">
        <div class="flex items-start gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2.5">
              <button
                class="group inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-se-gray-300 bg-white text-se-gray-700 transition-all hover:border-se-gray-400 hover:bg-se-gray-50 hover:text-se-gray-900 shrink-0"
                type="button"
                aria-label="Back to Leads & Projects Manager"
                @click="goBack"
              >
                <svg
                  class="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <path d="M10 4l-4 4 4 4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <button
                v-if="project.description"
                type="button"
                class="inline-flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md text-left text-gray-900 transition-colors hover:text-gray-700"
                @click="isDescriptionExpanded = !isDescriptionExpanded"
              >
                <UiText as="h1" variant="h5" class="font-semibold text-gray-900 truncate">
                  {{ project.title }}
                </UiText>
                <svg
                  class="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200"
                  :class="isDescriptionExpanded ? 'rotate-180' : ''"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
              <UiText v-else as="h1" variant="h5" class="font-semibold text-gray-900 truncate">
                {{ project.title }}
              </UiText>
              <span
                :class="statusPillClass"
                class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0 border capitalize"
              >
                {{ project.project_status.replace(/_/g, " ") }}
              </span>
              <div class="ml-auto relative" ref="menuRef">
                <button
                  class="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                  :class="{ 'bg-gray-100 text-gray-700!': menuOpen }"
                  @click="toggleMenu"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>

                <Transition
                  enter-active-class="transition duration-100 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition duration-75 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <div
                    v-if="menuOpen"
                    class="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-50 py-1 origin-top-right"
                  >
                    <button
                      v-for="item in menuItems"
                      :key="item.key"
                      class="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                      :class="item.variant === 'danger'
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-gray-50'"
                      @click="handleMenuAction(item.key)"
                    >
                      <UiIcon name="arrow-right" class="w-4 h-4 shrink-0 opacity-60" />
                      <UiText as="span" variant="p-small">{{ item.label }}</UiText>
                    </button>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>

        <div
          class="overflow-hidden transition-all duration-200 ease-in-out"
          :style="{ maxHeight: isDescriptionExpanded ? '300px' : '0px' }"
        >
          <div class="ml-10.5 pt-2 pb-3">
            <UiText
              as="p"
              variant="p-small"
              class="text-gray-600 leading-relaxed whitespace-pre-wrap max-w-2xl"
            >
              {{ project.description }}
            </UiText>
          </div>
        </div>
      </div>

      <div class="flex-1 flex flex-col min-h-0 project-detail-tabs">
        <UiTabs v-model="activeTab" default-value="chatroom">
          <UiTabsItem value="info" label="Info">
            <div class="flex-1 overflow-y-auto px-4">
              <div class="space-y-4 px-1 py-5">
                <div class="lg:hidden">
                  <AdminChatSidebar
                    :project="project"
                    bordered
                    @add-participant="isAddParticipantDialogOpen = true"
                    @remove-participant="handleRemoveParticipant"
                  />
                </div>
                <AdminProjectInfoTab :project="project" />
              </div>
            </div>
          </UiTabsItem>

          <UiTabsItem value="chatroom" label="Chatroom">
            <div class="flex-1 min-h-0 overflow-hidden p-4">
              <div class="flex h-full min-h-0 items-start gap-4 overflow-hidden">
                <div
                  class="relative flex-1 min-w-0 h-full min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <ChatFeed
                    :messages="messages"
                    :participants-display="null"
                    :current-user-id="currentUserId"
                    :current-org-id="currentOrgId"
                    side="admin"
                    :is-ready="areMessagesReady"
                    :is-loading-more="isLoadingMore"
                    :has-more="hasMore"
                    class="flex-1 min-h-0"
                    @load-more="loadMore"
                  />
                  <div
                    v-if="messagesLoadError"
                    class="border-t border-red-100 bg-red-50 px-4 py-3"
                  >
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <UiText as="p" variant="p-small" class="text-red-700">
                        {{ messagesLoadError }}
                      </UiText>
                      <UiButton
                        variant="outline"
                        size="sm"
                        label="Retry messages"
                        @click="refreshMessages"
                      />
                    </div>
                  </div>
                  <ChatComposer
                    :project-id="projectId"
                    side="admin"
                    :is-admin="true"
                    :acting-org-id="currentOrgId"
                    :thread-id="threadId ?? null"
                    :send-message="handleSendMessage"
                    :send-note="handleSendNote"
                    :upload-attachments="handleUpload"
                  />
                </div>

                <div class="hidden lg:block w-72 xl:w-80 shrink-0 self-start max-h-full">
                  <AdminChatSidebar
                    :project="project"
                    @add-participant="isAddParticipantDialogOpen = true"
                    @remove-participant="handleRemoveParticipant"
                  />
                </div>
              </div>
            </div>
          </UiTabsItem>

          <UiTabsItem value="quotes" label="Quotes" :count="project.quotes.length">
            <div class="grid gap-4 pt-4 lg:grid-cols-[minmax(0,1fr)_22rem] px-4">
              <section class="grid gap-4">
                <div v-for="quote in project.quotes" :key="quote.id" class="bg-white border border-gray-200 rounded-xl p-5">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <UiText as="h3" variant="h5" class="font-semibold">
                        Quote #{{ quote.id }}
                      </UiText>
                      <UiText as="p" variant="p-small" class="text-gray-500">
                        Created {{ formatDate(quote.created_at) }}
                      </UiText>
                    </div>
                    <div class="text-lg font-semibold text-gray-900">
                      {{ formatQuoteTotal(quote) }}
                    </div>
                  </div>

                  <div class="mt-4">
                    <QuoteDetailStrip :quote="quote" />
                    <div
                      v-if="quote.hours_bank_expiration_terms"
                      class="mt-3 rounded-lg bg-gray-50 px-4 py-3"
                    >
                      <UiText as="p" variant="caption-normal" class="mb-1 uppercase text-gray-500">
                        {{ quoteCopy.hoursBankExpirationTermsDisplayLabel }}
                      </UiText>
                      <p class="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                        {{ quote.hours_bank_expiration_terms }}
                      </p>
                    </div>
                  </div>

                  <div class="grid gap-4 mt-4 xl:grid-cols-2">
                    <div>
                      <UiText as="p" variant="caption-normal" class="uppercase text-gray-500 mb-2">Quote Status</UiText>
                      <UiSelect
                        v-model="quoteStatusDrafts[quote.id]"
                        :options="[
                          { value: 'offered', label: 'Offered' },
                          { value: 'accepted', label: 'Accepted' },
                          { value: 'rejected', label: 'Rejected' },
                          { value: 'cancelled', label: 'Cancelled' },
                        ]"
                      />
                      <UiButton
                        class="mt-3"
                        variant="outline"
                        :label="updateQuoteStatus.isPending.value ? 'Saving…' : 'Apply Quote Status'"
                        :disabled="updateQuoteStatus.isPending.value"
                        @click="applyQuoteStatus(quote.id)"
                      />
                    </div>

                    <div>
                      <UiText as="p" variant="caption-normal" class="uppercase text-gray-500 mb-2">Payment Status</UiText>
                      <UiSelect
                        v-model="quotePaymentDrafts[quote.id]"
                        :options="[
                          { value: '', label: 'Unset' },
                          { value: 'unpaid', label: 'Unpaid' },
                          { value: 'partial_paid', label: 'Partial Paid' },
                          { value: 'paid', label: 'Paid' },
                          { value: 'partial_refund', label: 'Partial Refund' },
                          { value: 'refund', label: 'Refund' },
                          { value: 'failed', label: 'Failed' },
                        ]"
                      />
                      <UiButton
                        class="mt-3"
                        variant="outline"
                        :label="updateQuotePaymentStatus.isPending.value ? 'Saving…' : 'Apply Payment Status'"
                        :disabled="updateQuotePaymentStatus.isPending.value"
                        @click="applyQuotePaymentStatus(quote.id)"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <AdminChatSidebar
                :project="project"
                bordered
                @add-participant="isAddParticipantDialogOpen = true"
                @remove-participant="handleRemoveParticipant"
              />
            </div>
          </UiTabsItem>

          <UiTabsItem value="status_history" label="Status History">
            <div class="grid gap-4 pt-4 lg:grid-cols-[minmax(0,1fr)_22rem] px-4">
              <section class="grid gap-3">
                <div v-for="event in project.events" :key="event.id" class="bg-white border border-gray-200 rounded-xl p-5">
                  <UiText as="p" variant="p" class="font-semibold text-gray-900">{{ event.event_type }}</UiText>
                  <UiText as="p" variant="p-small" class="mt-2 text-gray-500">
                    Actor user {{ event.actor_user_id ?? "—" }} · Org {{ event.actor_org_id ?? "—" }} · {{ formatDate(event.created_at) }}
                  </UiText>
                </div>
              </section>

              <AdminChatSidebar
                :project="project"
                bordered
                @add-participant="isAddParticipantDialogOpen = true"
                @remove-participant="handleRemoveParticipant"
              />
            </div>
          </UiTabsItem>
        </UiTabs>
      </div>
    </template>

    <UiDialog v-model="isAddParticipantDialogOpen">
      <div class="space-y-4 p-1">
        <UiText as="h3" variant="h5" class="font-semibold text-gray-900">
          Add Participant
        </UiText>

        <UiInputField v-model="newParticipantOrgId" label="Organization ID" placeholder="Enter organization id" />

        <UiSelect
          v-model="newParticipantGroup"
          label="Participant Group"
          :options="[
            { value: 'client', label: 'Client' },
            { value: 'expert', label: 'Expert' },
          ]"
        />

        <UiSelect
          v-model="newParticipantRole"
          label="Participant Role"
          :options="[
            { value: 'viewer', label: 'Viewer' },
            { value: 'contributor', label: 'Contributor' },
            { value: 'manager', label: 'Manager' },
            { value: 'owner', label: 'Owner' },
          ]"
        />

        <UiInputField
          v-model="newParticipantExpiry"
          label="Expiry (optional)"
          placeholder="YYYY-MM-DD HH:MM:SS"
        />

        <div class="flex justify-end gap-3">
          <UiButton variant="ghost" label="Cancel" @click="isAddParticipantDialogOpen = false" />
          <UiButton
            variant="primary"
            :label="addParticipant.isPending.value ? 'Adding…' : 'Add Participant'"
            :disabled="addParticipant.isPending.value"
            @click="submitParticipantDialog"
          />
        </div>
      </div>
    </UiDialog>
  </main>
</template>
